import { z } from "zod";
import { DbCreateInputOmit, DbUpdate } from "../../helpers";
import { OmitStrict } from "../../utils";

export const CodeComponentResolvedTypeKind = z.enum([
  "string",
  "number",
  "numberLiteral",
  "boolean",
  "booleanLiteral",
  "object",
  "function",
  "stringLiteral",
  "union",
  "slot",
  "null",
  "undefined",
  "any",
]);

export const CodeComponentResolvedType: z.ZodType = z.lazy(() =>
  z.object({
    kind: CodeComponentResolvedTypeKind,
    raw: z.string().optional(),
    types: z.array(CodeComponentResolvedType).optional(),
    isArray: z.boolean().optional(),
  })
);

export const CodeComponentParentType = z.object({
  fileName: z.string(),
  name: z.string(),
});

export const CodeComponentProperty = z.object({
  defaultValue: z.string().optional(),
  name: z.string(),
  required: z.boolean(),
  type: CodeComponentResolvedType,
  declarations: z.array(CodeComponentParentType).optional(),
  tags: z.record(z.string(), z.string()).optional(),
  description: z.string(),
});

export const CodeComponent = z.object({
  id: z.string(),
  designSystemVersionId: z.string(),
  persistentId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  exportName: z.string(),
  componentPath: z.string(),
  description: z.string(),
  properties: z.record(z.string(), CodeComponentProperty),
  tags: z.record(z.string(), z.string()).nullable(),
});

export const CodeComponentUpsertResponse = z.object({
  created: z.number().nonnegative(),
  updated: z.number().nonnegative(),
  deleted: z.number().nonnegative(),
});

export type CodeComponentResolvedType = z.infer<typeof CodeComponentResolvedType>;
export type CodeComponentParentType = z.infer<typeof CodeComponentParentType>;
export type CodeComponentProperty = z.infer<typeof CodeComponentProperty>;
export type CodeComponent = z.infer<typeof CodeComponent>;

export type CodeComponentCreate = DbCreateInputOmit<CodeComponent>;
export type CodeComponentUpdate = DbUpdate<CodeComponent>;
export type CodeComponentUpsert = OmitStrict<CodeComponentCreate, "persistentId" | "designSystemVersionId">;
export type CodeComponentUpsertResponse = z.infer<typeof CodeComponentUpsertResponse>;

//
// Analyze
//

export const RegistryType = z.enum(["Npm", "Github", "Artifactory", "Azure", "Custom"]);

export const Registry = z.object({
  type: RegistryType,
  url: z.string().optional(),
  accessToken: z.string().optional(),
  scope: z.string(),
  authConfig: z.record(z.string()).optional(),
});

export const DependencyDefinition = z.object({
  name: z.string(),
  version: z.string().optional(),
});

export const AnalyzeCodeComponentsInPackage = z.object({
  designSystemId: z.string(),
  versionId: z.string(),
  mainPackage: DependencyDefinition,
  dependencies: z.array(DependencyDefinition).optional(),
  registries: Registry.array(),
  userAccessToken: z.string(),
});

export type Registry = z.infer<typeof Registry>;
export type DependencyDefinition = z.infer<typeof DependencyDefinition>;
export type AnalyzeCodeComponentsInPackage = z.infer<typeof AnalyzeCodeComponentsInPackage>;
